import { Clock, Mail, MapPin, Phone } from 'lucide-react';
import { Link } from 'react-router';
import { ArrowIcon, ButtonLink } from '@/components/ui/Button';
import { DIVISION_PATH, useCategories } from '@/features/catalog/api';
import { useSite } from '@/features/site/SiteProvider';
import { uniquePhones } from '@/lib/utils';
import { NAV_ITEMS, Wordmark } from './SiteHeader';

function FooterHeading({ children }: { children: string }) {
  return <h2 className="mb-5 font-sans text-[12px] font-semibold tracking-[0.16em] text-white uppercase">{children}</h2>;
}

const linkClass = 'text-[14px] text-steel transition-colors hover:text-white';

export function SiteFooter() {
  const { settings, content } = useSite();
  const { contact, company } = settings;
  const { footer } = content;
  const categories = useCategories();
  const apparel = categories.data?.filter((c) => c.division === 'APPAREL') ?? [];
  const sports = categories.data?.filter((c) => c.division === 'SPORTS') ?? [];

  return (
    <footer className="bg-primary-dark text-steel">
      {/* Private-label call to action band */}
      <div className="border-b border-white/10">
        <div className="container-page flex flex-col gap-6 py-10 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="eyebrow text-accent-soft/80">Private label &amp; OEM</p>
            <p className="mt-2 font-display text-[22px] font-bold tracking-tight text-white md:text-[26px]">Planning a production run for your brand?</p>
          </div>
          <ButtonLink to="/contact#inquiry" variant="accent" size="lg" icon={<ArrowIcon />}>
            Request a Quote
          </ButtonLink>
        </div>
      </div>

      <div className="container-page grid gap-12 py-14 md:grid-cols-2 lg:grid-cols-12 lg:gap-8">
        <div className="lg:col-span-3">
          <Wordmark inverted />
          <p className="mt-6 max-w-[340px] text-[14px] leading-relaxed">{footer.about}</p>
          {footer.badges.length > 0 && (
            <ul className="mt-6 flex flex-wrap gap-2" aria-label="Registrations">
              {footer.badges.map((b) => (
                <li key={b.label} className="rounded-[4px] border border-white/15 px-2.5 py-1 text-[11px] font-semibold tracking-[0.12em] text-white uppercase">
                  {b.label}
                </li>
              ))}
            </ul>
          )}
        </div>

        <nav aria-label="Footer" className="lg:col-span-2">
          <FooterHeading>Company</FooterHeading>
          <ul className="space-y-3">
            {NAV_ITEMS.map((n) => (
              <li key={n.to}>
                <Link to={n.to} className={linkClass}>
                  {n.label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="lg:col-span-4">
          <FooterHeading>Products</FooterHeading>
          <div className="grid grid-cols-2 gap-6">
            <ul className="space-y-3">
              <li>
                <Link to={DIVISION_PATH.APPAREL} className="text-[14px] font-semibold text-white hover:underline">
                  Apparel
                </Link>
              </li>
              {apparel.map((c) => (
                <li key={c.slug}>
                  <Link to={`${DIVISION_PATH.APPAREL}/${c.slug}`} className={linkClass}>
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
            <ul className="space-y-3">
              <li>
                <Link to={DIVISION_PATH.SPORTS} className="text-[14px] font-semibold text-white hover:underline">
                  Sports goods
                </Link>
              </li>
              {sports.map((c) => (
                <li key={c.slug}>
                  <Link to={`${DIVISION_PATH.SPORTS}/${c.slug}`} className={linkClass}>
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="lg:col-span-3">
          <FooterHeading>Contact</FooterHeading>
          <ul className="space-y-4 text-[14px]">
            {contact.locations.slice(0, 1).map((l) => (
              <li key={l.label} className="flex gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-white/60" aria-hidden />
                <span>{l.address}</span>
              </li>
            ))}
            {contact.emails.map((e) => (
              <li key={e.email} className="flex gap-3">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-white/60" aria-hidden />
                <a href={`mailto:${e.email}`} className="break-all text-white transition-colors hover:text-accent-soft">
                  {e.email}
                </a>
              </li>
            ))}
            {uniquePhones(contact.phones).map((p) => (
              <li key={p.number} className="flex gap-3">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-white/60" aria-hidden />
                <a href={`tel:${p.number.replace(/[^\d+]/g, '')}`} className="text-white hover:text-accent-soft">
                  {p.number}
                </a>
              </li>
            ))}
            {contact.workingHours && (
              <li className="flex gap-3">
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-white/60" aria-hidden />
                <span>{contact.workingHours}</span>
              </li>
            )}
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-page flex flex-col gap-3 py-6 text-[12.5px] md:flex-row md:items-center md:justify-between">
          <p>
            © {new Date().getFullYear()} {company.legalName}. {footer.legalNotice}
          </p>
          <p className="flex flex-wrap gap-x-5 gap-y-2">
            <Link to="/privacy-policy" className="hover:text-white">
              Privacy policy
            </Link>
            <Link to="/terms" className="hover:text-white">
              Terms &amp; conditions
            </Link>
            <span className="text-steel">Photography: illustrative industry images (Unsplash &amp; Pexels licences), not our facilities</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
